import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { HomeIcon, ExternalLinkIcon } from '@radix-ui/react-icons';

export interface PropertyCardProps {
  agencyId: string;
  propertyId?: string;
  emptyLabel?: string;
  sectionLabel?: string;
}

interface PropertySnapshot {
  headline: string;
  address: string;
}

/**
 * Joins a property's street/number/neighborhood/city/state into a single
 * display address, skipping any parts that are missing.
 */
export function composeAddress(location: Record<string, string | undefined>): string {
  const street = location?.street ?? '';
  const number = location?.number ?? '';
  const streetLine = street && number ? `${street}, ${number}` : street;
  const parts = [streetLine, location?.neighborhood, location?.city, location?.state];
  return parts.filter(Boolean).join(' - ');
}

/**
 * Lazily fetches a property's headline/address snapshot for card display,
 * loading `PropertiesService` on demand so the card bundle stays light.
 */
function usePropertySnapshot(agencyId: string, propertyId?: string) {
  const [snapshot, setSnapshot] = useState<PropertySnapshot | null>(null);
  const [loading, setLoading] = useState(Boolean(propertyId));

  useEffect(() => {
    if (!propertyId || !agencyId) return;
    setLoading(true);
    import('@/services/requests/housien-services/PropertiesService')
      .then(({ PropertiesService }) => PropertiesService.getProperty(agencyId, propertyId))
      .then((data: unknown) => {
        const d = data as Record<string, unknown>;
        const desc = (d?.description as Record<string, string>) ?? {};
        const loc = (d?.location as Record<string, string>) ?? {};
        setSnapshot({ headline: desc.headline || propertyId, address: composeAddress(loc) });
      })
      .catch(() => setSnapshot(null))
      .finally(() => setLoading(false));
  }, [agencyId, propertyId]);

  return { snapshot, loading };
}

/**
 * Compact linked property summary: shows an empty/loading/linked state
 * depending on whether a `propertyId` is set and its snapshot has loaded.
 */
export function PropertyCard({ agencyId, propertyId, emptyLabel, sectionLabel }: PropertyCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { snapshot, loading } = usePropertySnapshot(agencyId, propertyId);
  const label = sectionLabel ?? t('propertyCard.label');

  if (!propertyId) return <EmptyPropertyCard label={label} emptyLabel={emptyLabel ?? t('propertyCard.empty')} />;
  if (loading) return <LoadingPropertyCard label={label} />;

  return (
    <LinkedPropertyCard
      label={label}
      headline={snapshot?.headline ?? propertyId}
      address={snapshot?.address ?? ''}
      onClick={() => navigate(`/app/agencies/${agencyId}/properties/${propertyId}`)}
    />
  );
}

/**
 * Card state shown when no `propertyId` is set.
 */
function EmptyPropertyCard({ label, emptyLabel }: { label: string; emptyLabel: string }) {
  return (
    <div className={cn(cardBase, 'opacity-60')}>
      <CardLabel label={label} />
      <Text className="text-[length:var(--text-meta)] text-(--text-tertiary)">{emptyLabel}</Text>
    </div>
  );
}

/**
 * Card state shown while the property snapshot is being fetched.
 */
function LoadingPropertyCard({ label }: { label: string }) {
  return (
    <div className={cardBase}>
      <CardLabel label={label} />
      <div className="h-3 w-32 rounded bg-(--surface-tertiary) animate-pulse" />
      <div className="h-2 w-20 rounded bg-(--surface-tertiary) animate-pulse mt-(--space-1)" />
    </div>
  );
}

/**
 * Card state shown once a property snapshot has loaded; clicking navigates
 * to the property record.
 */
function LinkedPropertyCard({ label, headline, address, onClick }: { label: string; headline: string; address: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(cardBase, 'cursor-pointer hover:border-(--seed-base) transition-[var(--transition-standard)] group')}
    >
      <div className="flex items-center justify-between">
        <CardLabel label={label} />
        <ExternalLinkIcon width={12} height={12} className="text-(--text-tertiary) opacity-0 group-hover:opacity-100 transition-[var(--transition-standard)] mb-(--space-3)" />
      </div>
      <Text className="text-[length:var(--text-body-sm)] font-medium text-(--text-primary)">{headline}</Text>
      {address && <Text className="text-[length:var(--text-meta)] text-(--text-tertiary)">{address}</Text>}
    </button>
  );
}

/**
 * Small uppercase icon + label header shared by every card state.
 */
function CardLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-(--space-2) mb-(--space-3)">
      <Text component="span" className="text-(--text-tertiary)"><HomeIcon width={12} height={12} /></Text>
      <Text className="text-[length:var(--text-meta)] text-(--text-tertiary) uppercase tracking-wider font-bold">{label}</Text>
    </div>
  );
}

const cardBase = cn(
  'flex flex-col gap-0.5 p-(--space-3) rounded-(--radius-md)',
  'border border-(--border-subtle) bg-(--surface-secondary)',
  'text-left w-full',
);
